import * as React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';

export const Dialog = DialogPrimitive.Root;
export const DialogTrigger = DialogPrimitive.Trigger;
export const DialogClose = DialogPrimitive.Close;
export const DialogPortal = DialogPrimitive.Portal;

export const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      'fixed inset-0 z-50 bg-black/50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
      className,
    )}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

/**
 * Tab-transition tracker. The original "modal disappears on tab switch"
 * bug stems from Radix's Dialog firing close events when Chrome restores
 * focus on tab-return — the browser briefly lands focus on `body` and
 * Radix sees it as "interaction outside the dialog."
 *
 * Earlier fixes used a single grace timestamp (lastVisibleAt). That's
 * racy because the order of `visibilitychange` and `focus` events isn't
 * guaranteed — sometimes `focus` fires (and Radix dispatches its close
 * event) BEFORE `visibilitychange`, so the grace check sees a stale value.
 *
 * This version tracks two signals:
 *   - `lastTransitionAt` — bumped on ANY visibility/focus/blur transition
 *   - `document.hidden` — hard-true while the tab is actually backgrounded
 *
 * Combined: we treat "the tab is hidden right now" or "we transitioned
 * within the last ~1500ms" as the suppression window. That's robust
 * against event-ordering races.
 */
let lastTransitionAt = 0;
if (typeof document !== 'undefined') {
  const bump = () => { lastTransitionAt = Date.now(); };
  document.addEventListener('visibilitychange', bump);
  window.addEventListener('focus', bump);
  // blur counts too — it's the leading edge of a tab switch, and any
  // close event that fires AFTER blur but BEFORE the tab actually hides
  // is also spurious.
  window.addEventListener('blur', bump);
}
const TAB_TRANSITION_GRACE_MS = 1500;

// Helper — true if we're currently in a tab-transition window. Either:
//   - the tab is actively hidden (any close event is impossible-to-be-real)
//   - we transitioned (visibility change, focus, or blur) within the
//     grace window (event-ordering race protection)
function inTabTransition(): boolean {
  if (typeof document !== 'undefined' && document.hidden) return true;
  return Date.now() - lastTransitionAt < TAB_TRANSITION_GRACE_MS;
}

export const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>
>(({
  className,
  children,
  onInteractOutside,
  onPointerDownOutside,
  onFocusOutside,
  onEscapeKeyDown,
  ...props
}, ref) => (
  <DialogPortal>
    <DialogOverlay />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        'fixed left-1/2 top-1/2 z-50 grid w-[calc(100vw-1.5rem)] max-w-lg -translate-x-1/2 -translate-y-1/2 gap-4 border bg-background p-4 shadow-lg sm:p-6 sm:rounded-lg max-h-[calc(100vh-3rem)] overflow-y-auto',
        className,
      )}
      // Belt + braces: Radix can fire ANY of these on tab return depending
      // on browser + focus chain (onFocusOutside is the obvious one, but in
      // some browsers onPointerDownOutside fires when the click that
      // initially brought focus back to the tab lands on `body`). We
      // suppress all three within the grace window. Outside it, every
      // dismiss path works as normal.
      onFocusOutside={(event) => {
        if (inTabTransition()) { event.preventDefault(); return; }
        onFocusOutside?.(event);
      }}
      onPointerDownOutside={(event) => {
        if (inTabTransition()) { event.preventDefault(); return; }
        onPointerDownOutside?.(event);
      }}
      onInteractOutside={(event) => {
        if (inTabTransition()) { event.preventDefault(); return; }
        onInteractOutside?.(event);
      }}
      onEscapeKeyDown={(event) => {
        // Same guard for completeness — Esc shouldn't normally fire on
        // tab return, but if a stuck key is involved, suppress.
        if (inTabTransition()) { event.preventDefault(); return; }
        onEscapeKeyDown?.(event);
      }}
      {...props}
    >
      {children}
      <DialogPrimitive.Close className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none">
        <X className="h-4 w-4" />
        <span className="sr-only">Close</span>
      </DialogPrimitive.Close>
    </DialogPrimitive.Content>
  </DialogPortal>
));
DialogContent.displayName = DialogPrimitive.Content.displayName;

export function DialogHeader({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('flex flex-col space-y-1.5 text-left', className)} {...props} />;
}

export function DialogFooter({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn('flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2', className)}
      {...props}
    />
  );
}

export const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn('text-lg font-semibold leading-none tracking-tight', className)}
    {...props}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

export const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn('text-sm text-muted-foreground', className)}
    {...props}
  />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;
